import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AlertTriangle, LogOut, Search, CheckCircle2, XCircle, Loader2, Scale } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_STYLE = {
  pending: { label: 'En cours', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  resolved: { label: 'Résolu', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cancelled: { label: 'Annulé', className: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const TYPE_STYLE = {
  late_warning: { label: 'Mise en demeure', icon: AlertTriangle, color: 'text-amber-600' },
  eviction_notice: { label: 'Libération des lieux', icon: LogOut, color: 'text-rose-600' },
};

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

const TenantNotices = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', status: '', q: '' });
  const [resolveCtx, setResolveCtx] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      const r = await api.get('/tenant-notices', { params });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [filters.type, filters.status]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((it) => {
    if (!filters.q) return true;
    const q = filters.q.toLowerCase();
    return (
      (it.tenant_name || '').toLowerCase().includes(q) ||
      (it.property_title || '').toLowerCase().includes(q) ||
      (it.motif_label || '').toLowerCase().includes(q)
    );
  });

  const submitResolve = async (status) => {
    if (!resolveCtx) return;
    try {
      await api.post(`/tenant-notices/${resolveCtx.id}/resolve`, { resolution_note: note, status });
      toast.success(status === 'resolved' ? 'Document marqué comme résolu' : 'Document annulé');
      setResolveCtx(null);
      setNote('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const stats = {
    total: items.length,
    pending: items.filter((i) => i.status === 'pending').length,
    warnings: items.filter((i) => i.type === 'late_warning').length,
    evictions: items.filter((i) => i.type === 'eviction_notice').length,
  };

  return (
    <div className="space-y-6 pb-10" data-testid="tenant-notices-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2B3A2C] flex items-center gap-2">
          <Scale className="w-7 h-7 text-[#B85C38]" /> Journal contentieux
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Historique complet des mises en demeure et demandes de libération des lieux.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="text-xs uppercase font-bold text-gray-500">Total</div><div className="text-2xl font-bold text-[#2B3A2C] mt-1">{stats.total}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs uppercase font-bold text-gray-500">En cours</div><div className="text-2xl font-bold text-amber-700 mt-1">{stats.pending}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs uppercase font-bold text-gray-500">Mises en demeure</div><div className="text-2xl font-bold text-amber-800 mt-1">{stats.warnings}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs uppercase font-bold text-gray-500">Libérations</div><div className="text-2xl font-bold text-rose-700 mt-1">{stats.evictions}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription>Filtrez, consultez le détail et clôturez les dossiers résolus.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs font-semibold">Recherche</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                <Input
                  placeholder="Locataire, bien, motif..."
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  className="pl-8"
                  data-testid="notices-search"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Type</Label>
              <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="border rounded-md px-2 py-2 text-sm bg-white" data-testid="notices-type-filter">
                <option value="">Tous</option>
                <option value="late_warning">Mise en demeure</option>
                <option value="eviction_notice">Libération des lieux</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Statut</Label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="border rounded-md px-2 py-2 text-sm bg-white" data-testid="notices-status-filter">
                <option value="">Tous</option>
                <option value="pending">En cours</option>
                <option value="resolved">Résolu</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>
          </div>

          {loading ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" /></div>
            : filtered.length === 0 ? <p className="text-sm text-gray-500 text-center py-6">Aucun document dans le journal.</p>
            : (
              <div className="space-y-2">
                {filtered.map((it) => {
                  const T = TYPE_STYLE[it.type] || TYPE_STYLE.late_warning;
                  const S = STATUS_STYLE[it.status] || STATUS_STYLE.pending;
                  const Icon = T.icon;
                  return (
                    <div key={it.id} className="border border-[#B85C38]/20 rounded-lg p-3 hover:bg-[#F5EFE1]/30 transition" data-testid={`notice-${it.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Icon className={`w-4 h-4 ${T.color}`} />
                            <b className="text-[#2B3A2C]">{T.label}</b>
                            <Badge className={`border ${S.className}`}>{S.label}</Badge>
                          </div>
                          <div className="text-sm mt-1">
                            <b>{it.tenant_name || '—'}</b>
                            {it.property_title && <> · <span className="text-gray-700">{it.property_title}</span></>}
                          </div>
                          {it.type === 'late_warning' && (
                            <div className="text-xs text-gray-600 mt-0.5">
                              {it.amount_overdue ? <>Montant : <b>{fmt(it.amount_overdue)} FCFA</b></> : null}
                              {it.days_overdue ? <> · {it.days_overdue} jour(s) de retard</> : null}
                              {it.grace_period_days ? <> · Délai accordé : {it.grace_period_days} j</> : null}
                            </div>
                          )}
                          {it.type === 'eviction_notice' && (
                            <div className="text-xs text-gray-600 mt-0.5">
                              Motif : <b>{it.motif_label}</b>
                              {it.motif_detail && <> — <i>« {it.motif_detail} »</i></>}
                              {it.vacate_deadline_date && <> · À libérer avant le <b>{fmtDate(it.vacate_deadline_date)}</b></>}
                            </div>
                          )}
                          <div className="text-[11px] text-gray-500 mt-1">
                            Émis le {fmtDT(it.created_at)} par {it.created_by_name || '—'}
                            {it.resolved_at && <> · Clôturé le {fmtDT(it.resolved_at)} par {it.resolved_by_name || '—'}</>}
                            {it.resolution_note && <> · <i>« {it.resolution_note} »</i></>}
                          </div>
                        </div>
                        {it.status === 'pending' && (
                          <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" onClick={() => { setResolveCtx(it); setNote(''); }} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid={`resolve-${it.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Clôturer
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </CardContent>
      </Card>

      <Dialog open={!!resolveCtx} onOpenChange={(o) => !o && setResolveCtx(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" /> Clôturer le dossier
            </DialogTitle>
            <DialogDescription>
              {resolveCtx && <>Locataire : <b>{resolveCtx.tenant_name}</b> · {TYPE_STYLE[resolveCtx.type]?.label}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold">Note de clôture (optionnelle)</Label>
            <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Locataire a régularisé le 15/03, dossier clos." data-testid="resolve-note" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => submitResolve('cancelled')} className="border-gray-400" data-testid="cancel-notice-btn">
              <XCircle className="w-4 h-4 mr-1" /> Annuler le dossier
            </Button>
            <Button onClick={() => submitResolve('resolved')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-resolve-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Marquer résolu
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantNotices;
